import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PersonSection } from '../../types/person.js';
import * as personApi from '../../services/person-api.js';

@customElement('cp-person-section-editor')
export class CpPersonSectionEditor extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) personId = 0;
  @property({ type: Array }) sections: PersonSection[] = [];

  @state() private _editingId: number | null = null;
  @state() private _editTitle = '';
  @state() private _editContent = '';
  @state() private _addingNew = false;
  @state() private _newTitle = '';
  @state() private _newContent = '';
  @state() private _saving = false;
  @state() private _confirmDeleteId: number | null = null;

  private _startEdit(section: PersonSection): void {
    this._editingId = section.id;
    this._editTitle = section.title;
    this._editContent = section.content;
    this._confirmDeleteId = null;
  }

  private _cancelEdit(): void {
    this._editingId = null;
  }

  private async _saveEdit(): Promise<void> {
    if (!this._editingId) return;
    this._saving = true;
    try {
      await personApi.updateSection(this.personId, this._editingId, {
        title: this._editTitle,
        content: this._editContent,
      });
      this._editingId = null;
      this._notifyUpdated();
    } catch (e) {
      console.error('Failed to update section:', e);
    } finally {
      this._saving = false;
    }
  }

  private _confirmDelete(id: number): void {
    this._confirmDeleteId = id;
  }

  private async _deleteSection(id: number): Promise<void> {
    this._saving = true;
    try {
      await personApi.deleteSection(this.personId, id);
      this._confirmDeleteId = null;
      this._notifyUpdated();
    } catch (e) {
      console.error('Failed to delete section:', e);
    } finally {
      this._saving = false;
    }
  }

  private async _addSection(): Promise<void> {
    if (!this._newTitle.trim()) return;
    this._saving = true;
    try {
      await personApi.addSection(this.personId, {
        title: this._newTitle,
        content: this._newContent,
      });
      this._addingNew = false;
      this._newTitle = '';
      this._newContent = '';
      this._notifyUpdated();
    } catch (e) {
      console.error('Failed to add section:', e);
    } finally {
      this._saving = false;
    }
  }

  private _notifyUpdated(): void {
    this.dispatchEvent(new CustomEvent('sections-updated', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <div class="cp-person-sections">
        <div class="cp-person-sections__header">
          <h2 class="cp-person-sections__title">Sektionen</h2>
          <button
            class="cp-person-sections__add-btn"
            @click=${() => { this._addingNew = true; this._editingId = null; }}
            ?disabled=${this._addingNew}
          >+ Sektion</button>
        </div>

        ${this._addingNew ? this._renderNewSectionForm() : nothing}

        ${this.sections.length === 0 && !this._addingNew ? html`
          <p class="cp-person-sections__empty">Noch keine Sektionen angelegt.</p>
        ` : nothing}

        ${this.sections.map(section =>
          this._editingId === section.id
            ? this._renderEditForm(section)
            : this._renderSection(section)
        )}
      </div>
    `;
  }

  private _renderSection(section: PersonSection) {
    return html`
      <div class="cp-person-section">
        <div class="cp-person-section__header">
          <h3 class="cp-person-section__title">${section.title}</h3>
          <div class="cp-person-section__actions">
            <button
              class="cp-person-section__edit-btn"
              @click=${() => this._startEdit(section)}
              ?disabled=${this._saving}
            >Bearbeiten</button>
            ${this._confirmDeleteId === section.id ? html`
              <span class="cp-person-section__confirm-text">Wirklich löschen?</span>
              <button
                class="cp-person-section__confirm-delete-btn"
                @click=${() => void this._deleteSection(section.id)}
                ?disabled=${this._saving}
              >Ja</button>
              <button
                class="cp-person-section__cancel-delete-btn"
                @click=${() => { this._confirmDeleteId = null; }}
              >Nein</button>
            ` : html`
              <button
                class="cp-person-section__delete-btn"
                @click=${() => this._confirmDelete(section.id)}
                ?disabled=${this._saving}
              >Löschen</button>
            `}
          </div>
        </div>
        ${section.content ? html`
          <pre class="cp-person-section__content">${section.content}</pre>
        ` : nothing}
      </div>
    `;
  }

  private _renderEditForm(section: PersonSection) {
    return html`
      <div class="cp-person-section cp-person-section--editing">
        <input
          class="cp-person-section__title-input"
          type="text"
          .value=${this._editTitle}
          @input=${(e: Event) => { this._editTitle = (e.target as HTMLInputElement).value; }}
          placeholder="Titel"
        />
        <textarea
          class="cp-person-section__content-input"
          .value=${this._editContent}
          @input=${(e: Event) => { this._editContent = (e.target as HTMLTextAreaElement).value; }}
          placeholder="Inhalt (Markdown)"
          rows="6"
        ></textarea>
        <div class="cp-person-section__edit-actions">
          <button
            class="cp-person-section__cancel-btn"
            @click=${this._cancelEdit}
            ?disabled=${this._saving}
          >Abbrechen</button>
          <button
            class="cp-person-section__save-btn"
            @click=${() => void this._saveEdit()}
            ?disabled=${this._saving}
          >${this._saving ? 'Speichern...' : 'Speichern'}</button>
        </div>
      </div>
    `;
  }

  private _renderNewSectionForm() {
    return html`
      <div class="cp-person-section cp-person-section--new">
        <input
          class="cp-person-section__title-input"
          type="text"
          .value=${this._newTitle}
          @input=${(e: Event) => { this._newTitle = (e.target as HTMLInputElement).value; }}
          placeholder="Titel der Sektion"
          autofocus
        />
        <textarea
          class="cp-person-section__content-input"
          .value=${this._newContent}
          @input=${(e: Event) => { this._newContent = (e.target as HTMLTextAreaElement).value; }}
          placeholder="Inhalt (Markdown, optional)"
          rows="4"
        ></textarea>
        <div class="cp-person-section__edit-actions">
          <button
            class="cp-person-section__cancel-btn"
            @click=${() => { this._addingNew = false; this._newTitle = ''; this._newContent = ''; }}
          >Abbrechen</button>
          <button
            class="cp-person-section__save-btn"
            @click=${() => void this._addSection()}
            ?disabled=${this._saving || !this._newTitle.trim()}
          >${this._saving ? 'Speichern...' : 'Hinzufügen'}</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-person-section-editor': CpPersonSectionEditor;
  }
}
